import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { company } from '../data/company';
import { whatsappUrl, whatsappUrlFor, quotePath } from '../utils/contactLinks';
import './ContactCard.css';

const EMAILS = [
  { address: company.emails.info, label: 'General enquiries' },
  { address: company.emails.accounts, label: 'Accounts' },
];

// Address, hours, phones and email in one place — the info visitors most often look for.
// `detailed` (Contact page): Call + WhatsApp per number and every email address.
function ContactCard({ title = 'Visit or call us', showQuote = true, detailed = false }) {
  return (
    <aside className={`contact-card${detailed ? ' contact-card-detailed' : ''}`} data-track-location="contact_card">
      <h3>{title}</h3>
      <ul className="contact-card-list">
        <li>
          <Icon name="pin" />
          <div>
            <a href={company.mapsUrl} target="_blank" rel="noopener noreferrer">
              {company.address.line1}, {company.address.line2}, {company.address.locality}
            </a>
          </div>
        </li>
        <li>
          <Icon name="clock" />
          <div>
            Mon–Thu {company.hours.weekdays}
            <br />
            Fri {company.hours.friday}
          </div>
        </li>
        {!detailed && (
          <li>
            <Icon name="phone" />
            <div>
              {company.phones.map((phone) => (
                <a
                  key={phone.href}
                  href={phone.href}
                  className="contact-card-phone"
                >
                  <strong>{phone.number}</strong> <span>{phone.label}</span>
                </a>
              ))}
            </div>
          </li>
        )}
        {!detailed && (
          <li>
            <Icon name="mail" />
            <div>
              <a href={`mailto:${company.email}`}>
                {company.email}
              </a>
            </div>
          </li>
        )}
      </ul>

      {detailed && (
        <>
          <h4 className="contact-card-sub">Phone &amp; WhatsApp</h4>
          <ul className="contact-rows">
            {company.phones.map((phone) => {
              const wa = whatsappUrlFor(phone);
              return (
                <li key={phone.href} className="contact-row">
                  <div className="contact-row-text">
                    <small>{phone.label}</small>
                    <strong>{phone.number}</strong>
                  </div>
                  <div className="contact-row-actions">
                    <a
                      href={phone.href}
                      className="icon-btn"
                      aria-label={`Call ${phone.label} on ${phone.number}`}
                      title="Call"
                    >
                      <Icon name="phone" size={18} />
                    </a>
                    <a
                      href={wa}
                      className="icon-btn icon-btn-whatsapp"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`WhatsApp ${phone.label} on ${phone.number}`}
                      title="WhatsApp"
                    >
                      <Icon name="whatsapp" size={18} />
                    </a>
                  </div>
                </li>
              );
            })}
          </ul>

          <h4 className="contact-card-sub">Email</h4>
          <ul className="contact-rows">
            {EMAILS.filter((e) => e.address).map((email) => (
              <li key={email.address} className="contact-row">
                <div className="contact-row-text">
                  <small>{email.label}</small>
                  <strong>{email.address}</strong>
                </div>
                <div className="contact-row-actions">
                  <a
                    href={`mailto:${email.address}`}
                    className="icon-btn"
                    aria-label={`Email ${email.address}`}
                    title="Email"
                  >
                    <Icon name="mail" size={18} />
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {(showQuote || !detailed) && (
        <div className="contact-card-actions">
          {showQuote && (
            <Link to={quotePath} className="btn btn-primary btn-block">
              Request a quote <Icon name="arrow" />
            </Link>
          )}
          {!detailed && (
            <a
              href={whatsappUrl}
              className="btn btn-outline btn-block"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="whatsapp" /> WhatsApp us
            </a>
          )}
        </div>
      )}
    </aside>
  );
}

export default ContactCard;
